import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { useState, type FC } from "react";
import { useAiUsageQuery } from "../../../api/entities/company.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { SettingsSection } from "../shared/settingsSection.js";
import { AiCreditsSection } from "./aiCreditsSection.js";
import { CreditUsageHeader } from "./creditUsageSection.js";
import { PlanSection } from "./planSection.js";
import { VoiceCreditsSection } from "./voiceCreditsSection.js";

/**
 * The redesigned billing page (plan summary + AI usage), shown when the
 * `new-billing` flag is on — see {@link BillingSettingsGate}.
 */
export const BillingSettingsPage: FC = () => {
  // The voice usage breakdown only applies to companies with the Voice agents
  // workspace.
  const hasVoiceAgents = useIsFeatureFlagEnabled(FeatureFlagKey.AgentsTab);
  const [periodOffset, setPeriodOffset] = useState(0);
  // Current-period usage; deduped with the sections' own queries.
  const { currentData: currentAiUsage } = useAiUsageQuery({ periodOffset: 0 });

  return (
    <SettingsSection>
      <div className="space-y-6">
        <header>
          <h1 className="text-xl leading-6 font-semibold text-foreground">
            Billing
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your plan, invoices, and AI usage.
          </p>
        </header>

        <PlanSection />

        <div className="space-y-5">
          <CreditUsageHeader
            onPeriodOffsetChange={setPeriodOffset}
            periodOffset={periodOffset}
            periods={currentAiUsage?.periods}
          />
          <InfoBanner
            title="Usage preview"
            description="Usage is tracked, but nothing is billed or paused based on it."
          />
        </div>
        <AiCreditsSection periodOffset={periodOffset} />
        {hasVoiceAgents ? (
          <VoiceCreditsSection periodOffset={periodOffset} />
        ) : null}
      </div>
    </SettingsSection>
  );
};
